import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from './AuthContext';
import { FEATURE_FLAG_DEFAULTS } from '../constants/featureFlags';

export interface LicenseStatus {
  licensingEnabled: boolean;
  valid: boolean;
  machineId: string;
  message?: string;
  expiresAt?: string | null;
  customer?: string;
}

interface SystemContextType {
  flags: Record<string, boolean>;
  flagsMeta: Record<string, { label: string; grupo: string; descripcion: string }>;
  license: LicenseStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
  isEnabled: (key: string) => boolean;
}

const SystemContext = createContext<SystemContextType | undefined>(undefined);

export const SystemProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>({ ...FEATURE_FLAG_DEFAULTS });
  const [flagsMeta, setFlagsMeta] = useState<SystemContextType['flagsMeta']>({});
  const [license, setLicense] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const licRes = await axios.get(`${API_URL}/licensing/status`);
      setLicense(licRes.data);
    } catch {
      setLicense({ licensingEnabled: false, valid: true, machineId: '', message: 'Sin datos de licencia' });
    }
    if (token) {
      try {
        const fRes = await axios.get(`${API_URL}/configuracion/features`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFlags({ ...FEATURE_FLAG_DEFAULTS, ...(fRes.data?.flags || {}) });
        setFlagsMeta(fRes.data?.meta || {});
      } catch {
        /* defaults */
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isEnabled = useCallback((key: string) => !!flags[key], [flags]);

  return (
    <SystemContext.Provider value={{ flags, flagsMeta, license, loading, refresh, isEnabled }}>
      {children}
    </SystemContext.Provider>
  );
};

export function useSystem() {
  const ctx = useContext(SystemContext);
  if (!ctx) throw new Error('useSystem debe usarse dentro de SystemProvider');
  return ctx;
}

export function useFeatureFlag(key: string): boolean {
  const { isEnabled } = useSystem();
  return isEnabled(key);
}
